import { useTranslation } from "react-i18next";
import camelCase from "lodash/camelCase";
import { useNavigate } from "react-router-dom";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";

import { EntityIcons } from "../../../../../utils/GlobalIcons";
import { useAppSelector } from "../../../../../redux/store";
import { reportSliceSelectors } from "../../../../../redux/report/report.selector";
import { taskMetricKeys, widgetTitleIconsAndColors } from "./types";
import { ThemeType } from "../../../../../theme/Theme";
import {
  getRangeLabel,
  onViewEntitiesFromCards,
} from "../../../../../utils/Reports";
import { ReportKinds } from "../../../../../model/reports/types/ReportsKinds.enum";
import { ActivityEntityKind } from "../../../../../model/activity/types/ActivityEntityKind.enum";
import { ReportsSummarySection, SummaryMetric } from "./ReportsSummarySection";
import { ReportTrendChart } from "../../ReportTrendChart";
import { TaskFilterReminder } from "../../../../../model/task/types/TaskFilterReminder.enum";

/** Each task figure covers its own window; none is a slice of another. */
const SUBTITLE_KEY: Record<string, string> = {
  [ReportKinds.TASK]: "reports.metric.createdInRange",
  [ReportKinds.TASK_PENDING]: "reports.metric.openNow",
  [ReportKinds.TASK_COMPLETED]: "reports.metric.completedInRange",
};

export const ReportsSummaryTaskTab: React.FC = () => {
  const { t } = useTranslation();
  const reports = useAppSelector(reportSliceSelectors.selectReport);
  const navigate = useNavigate();

  const rangeLabel = getRangeLabel(reports, t);

  const metrics: SummaryMetric[] = taskMetricKeys.map((key) => ({
    key,
    title: t(`reports.${camelCase(key)}.title`),
    subtitle: `${t(SUBTITLE_KEY[key])} · ${rangeLabel}`,
    value: reports.metrics?.[key] ?? 0,
    previousValue: reports.previousMetrics?.[key],
    higherIsWorse: key === ReportKinds.TASK_PENDING,
    color: widgetTitleIconsAndColors[key].color as keyof ThemeType,
    icon: widgetTitleIconsAndColors[key].icon,
    emphasize: key === ReportKinds.TASK,
    onView: () =>
      onViewEntitiesFromCards(
        {
          range: reports?.range,
          scope: reports?.scope,
          ...(key === ReportKinds.TASK
            ? {}
            : { done: key === ReportKinds.TASK_COMPLETED }),
        },
        ReportKinds.TASK,
        navigate,
      ),
  }));

  // A standing figure rather than a windowed one, so it carries no delta: the
  // previous period has no "still overdue right now" to compare against.
  metrics.push({
    key: "TASK_OVERDUE",
    title: t("reports.taskOverdue.title"),
    subtitle: t("reports.metric.overdueNow"),
    value: reports.tasksOverdue ?? 0,
    higherIsWorse: true,
    color: "danger",
    icon: faTriangleExclamation,
    onView: () =>
      onViewEntitiesFromCards(
        { reminder: TaskFilterReminder.OVERDUE },
        ReportKinds.TASK,
        navigate,
      ),
  });

  return (
    <ReportsSummarySection
      title={t("common.tasks")}
      icon={EntityIcons.Tasks}
      info={t("reports.tabs.summary.subtab.task.info")}
      metrics={metrics}
      columns={4}
      footer={<ReportTrendChart kind={ActivityEntityKind.TASK} />}
    />
  );
};
